// errorReporter.ts
// Rapporte une erreur client vers /api/telemetry/errors. Fire-and-forget : ne
// doit jamais faire échouer ni bloquer l'appelant (ErrorBoundary, handlers globaux…).

import { TELEMETRY_SOURCES } from "@/domain/constants/telemetry-sources";
import { postErrorReport } from "@/infrastructure/api/telemetry.api";

const ERROR_REPORT_DEDUPE_WINDOW_MS = 1_000;
const MAX_EXCEPTION_MESSAGE_LENGTH = 4_000;
const MAX_EXCEPTION_STACK_LENGTH = 12_000;
const MAX_METADATA_VALUE_LENGTH = 2_000;
const recentErrorReports = new Map<string, number>();

interface ErrorDetails {
  exceptionType?: string;
  exceptionMessage: string;
  exceptionStack?: string;
  metadata: Record<string, string>;
}

function truncate(value: string, maxLength: number): string {
  return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value;
}

function stringifyValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (value instanceof Error) return value.message;

  try {
    const serialized = JSON.stringify(value);
    return serialized ?? String(value);
  } catch {
    return String(value);
  }
}

function getRuntimeMetadata(): Record<string, string> {
  if (!("window" in globalThis)) return {};

  const { document, navigator, location, screen } = globalThis.window;
  return {
    pageUrl: `${location.origin}${location.pathname}`,
    userAgent: navigator.userAgent,
    language: navigator.language,
    viewport: `${globalThis.window.innerWidth}x${globalThis.window.innerHeight}`,
    screen: `${screen.width}x${screen.height}`,
    online: String(navigator.onLine),
    referrer: document.referrer,
  };
}

function getErrorDetails(error: unknown): ErrorDetails {
  if (!(error instanceof Error)) {
    return {
      exceptionType:
        error !== null && typeof error === "object"
          ? error.constructor.name
          : undefined,
      exceptionMessage: truncate(stringifyValue(error), MAX_EXCEPTION_MESSAGE_LENGTH),
      metadata: {},
    };
  }

  const metadata: Record<string, string> = {};
  const errorWithCause = error as Error & { cause?: unknown };
  if (errorWithCause.cause !== undefined) {
    metadata.errorCause = truncate(
      stringifyValue(errorWithCause.cause),
      MAX_METADATA_VALUE_LENGTH,
    );
  }

  return {
    exceptionType: error.name,
    exceptionMessage: truncate(error.message, MAX_EXCEPTION_MESSAGE_LENGTH),
    exceptionStack: error.stack
      ? truncate(error.stack, MAX_EXCEPTION_STACK_LENGTH)
      : undefined,
    metadata,
  };
}

function shouldReportError(fingerprint: string): boolean {
  const now = Date.now();

  for (const [recentFingerprint, reportedAt] of recentErrorReports) {
    if (now - reportedAt >= ERROR_REPORT_DEDUPE_WINDOW_MS) {
      recentErrorReports.delete(recentFingerprint);
    }
  }

  const lastReportedAt = recentErrorReports.get(fingerprint);
  if (
    lastReportedAt !== undefined &&
    now - lastReportedAt < ERROR_REPORT_DEDUPE_WINDOW_MS
  ) {
    return false;
  }

  recentErrorReports.set(fingerprint, now);
  return true;
}

export function reportClientError(
  source: string,
  error: unknown,
  metadata?: Record<string, string>,
): void {
  const details = getErrorDetails(error);
  const reportMetadata = Object.fromEntries(
    Object.entries({
      ...getRuntimeMetadata(),
      ...details.metadata,
      ...metadata,
    }).map(([key, value]) => [
      key,
      truncate(String(value), MAX_METADATA_VALUE_LENGTH),
    ]),
  );
  const fingerprint = JSON.stringify([
    details.exceptionType ?? "",
    details.exceptionMessage,
    details.exceptionStack ?? "",
  ]);

  if (!shouldReportError(fingerprint)) return;

  postErrorReport({
    source,
    exceptionType: details.exceptionType,
    exceptionMessage: details.exceptionMessage,
    exceptionStack: details.exceptionStack,
    metadata: reportMetadata,
  }).catch(() => {
    // La télémétrie ne doit jamais faire échouer l'appelant.
  });
}

/** Capture les erreurs non gérées (uncaught + promesses rejetées) au niveau window. */
export function setupGlobalErrorReporting(): void {
  if (!("window" in globalThis)) return;

  globalThis.window.addEventListener("error", (event: ErrorEvent) => {
    reportClientError(
      TELEMETRY_SOURCES.globalError,
      event.error ?? event.message,
      {
        eventMessage: event.message,
        fileName: event.filename,
        lineNumber: String(event.lineno),
        columnNumber: String(event.colno),
      },
    );
  });

  globalThis.window.addEventListener(
    "unhandledrejection",
    (event: PromiseRejectionEvent) => {
      reportClientError(TELEMETRY_SOURCES.unhandledRejection, event.reason, {
        rejectionEvent: "unhandledrejection",
      });
    },
  );
}
